import { format } from "date-fns"
import { de } from "date-fns/locale"
import { CalendarIcon, PlusCircle, XCircle } from "lucide-react"
import * as React from "react"
import type { DateRange } from "react-day-picker"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"

interface DateRangeFilterProps {
  title: string
  value: DateRange | undefined
  onChange: (range: DateRange | undefined) => void
}

export function DateRangeFilter({
  title,
  value,
  onChange,
}: DateRangeFilterProps) {
  const { translate, currentLanguage } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const hasValue = value?.from || value?.to
  const dateFnsLocale = currentLanguage === "DE" ? { locale: de } : undefined
  const dateFormat = currentLanguage === "DE" ? "d. MMM yyyy" : "MMM d, yyyy"

  const onReset = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation()
      onChange(undefined)
    },
    [onChange],
  )

  const formatLabel = () => {
    if (!value?.from) return null
    if (value.to) {
      return `${format(value.from, dateFormat, dateFnsLocale)} - ${format(value.to, dateFormat, dateFnsLocale)}`
    }
    return format(value.from, dateFormat, dateFnsLocale)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-dashed font-normal"
        >
          {hasValue ? (
            <div
              role="button"
              aria-label={translate("FILTERS/CLEAR_FILTER_ARIA", { title })}
              tabIndex={0}
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
              onClick={onReset}
            >
              <XCircle />
            </div>
          ) : (
            <PlusCircle />
          )}
          {hasValue ? (
            <>
              <CalendarIcon className="size-3.5" />
              <Separator
                orientation="vertical"
                className="mx-0.5 data-[orientation=vertical]:h-4"
              />
              <span className="text-xs">{formatLabel()}</span>
            </>
          ) : (
            title
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={value}
          onSelect={(range) => onChange(range)}
          numberOfMonths={2}
        />
        {hasValue && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                onChange(undefined)
                setOpen(false)
              }}
            >
              {translate("KNOWLEDGEBASE/CLEAR_DATE_FILTER_ACTION")}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
